import PropTypes from 'prop-types';
import './Form.css';

/**
 * Stacked subtotals + grand-total block (e.g. "Plan mensual / IVA / Total").
 *
 * @param {object} props
 * @param {Array<{ id?: string|number, label: React.ReactNode, value: React.ReactNode }>} props.rows
 *   Subtotal rows rendered in order. `id` is preferred as the React key;
 *   `label` is used as a fallback. Pass `[]` for a grand-total-only block.
 * @param {{ label: React.ReactNode, value: React.ReactNode }} [props.grand]
 *   Optional grand-total row, rendered with emphasis. Intentionally
 *   optional so the block can also display as "subtotals only" — e.g.
 *   when the grand total is shown elsewhere on the page (a CTA button)
 *   or hasn't been computed yet (loading state). When omitted, the
 *   trailing border/spacing for the grand row is also omitted.
 *
 * @example
 * <Totals
 *   rows={[
 *     { label: 'Plan mensual', value: '$249.000' },
 *     { label: 'IVA · 19%',    value: '$47.310' },
 *   ]}
 *   grand={{ label: 'Total a cobrar', value: '$296.310' }}
 * />
 */
export function Totals({ rows = [], grand }) {
  return (
    <div className="totals">
      {rows.map((row) => (
        <div key={row.id != null ? row.id : row.label} className="totals-row">
          <span className="lbl">{row.label}</span>
          <span className="v">{row.value}</span>
        </div>
      ))}
      {grand && (
        <div className="totals-row grand">
          <span className="lbl">{grand.label}</span>
          <span className="v">{grand.value}</span>
        </div>
      )}
    </div>
  );
}

Totals.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      /** Optional stable id; falls back to `label` as the React key. */
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.node,
      value: PropTypes.node,
    }),
  ),
  grand: PropTypes.shape({
    label: PropTypes.node,
    value: PropTypes.node,
  }),
};

export default Totals;
